import PlaceOrderState, { initialState } from "@affinity/Store/AffinityPlaceOrderState";
import { AddressViewModel, AffinityDelivery, AffinityProduct, HarrierPricingModel, LastPurchased, PlaceOrderStep, PriceOptionViewModel, ProductModelX, ProductViewModel, QuantityViewModel } from "@affinity/Store/StoreTypes";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { Category } from "@insite/client-framework/Services/CategoryService";
import placeOrder from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";
import { CategoryModel, ProductModel, ProductPriceDto, ShipToCollectionModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { Draft, isDraft } from "immer";

const reducer = {
    "Affinity/PlaceOrder/Initialise": (
        draft: Draft<PlaceOrderState>,
        action: { productRegular: ProductModel, productPremium: ProductModel, quantityRegular: QuantityViewModel, quantityPremium: QuantityViewModel }
    ) => {
        draft.currentStep = PlaceOrderStep.SelectProduct;
        draft.productRegular = action.productRegular;
        draft.productPremium = action.productPremium;
        draft.quantityRegular = action.quantityRegular;
        draft.quantityPremium = action.quantityPremium;
        draft.stepValues = {
            [PlaceOrderStep.SelectProduct]: undefined,
            [PlaceOrderStep.SelectQuantity]: undefined,
            [PlaceOrderStep.SelectAddress]: undefined,
            [PlaceOrderStep.SelectPriceAndDeliveryOptions]: undefined,
            [PlaceOrderStep.ReviewOrder]: undefined,
            [PlaceOrderStep.ConfirmationState]: undefined,
            [PlaceOrderStep.Payment]: undefined,
            [PlaceOrderStep.Terminal]: undefined,
        };
    },
    "Affinity/Model/Quantity/Set": (
        draft: Draft<PlaceOrderState>,
        action: { productModel: ProductModel, quantityModel: QuantityViewModel, productDescription: string },
    ) => {
        draft.viewModelQuantity = action.quantityModel;
    },
    "Affinity/Model/Address/Set": (
        draft: Draft<PlaceOrderState>,
        action: { addressesRaw: ShipToModel[], addressesModel: AddressViewModel[], quantityDescription: string }
    ) => {
        draft.shipToRaw = action.addressesRaw;
        draft.shipToMapped = action.addressesModel;
        draft.quantityDescription = action.quantityDescription;
    },
    "Affinity/PlaceOrder/Pricing/Update": (
        draft: Draft<PlaceOrderState>,
        action: { deliveryCheapest: PriceOptionViewModel, deliverySoonest: PriceOptionViewModel, hasAttemped: Boolean, pricingRaw: HarrierPricingModel },
    ) => {
        draft.deliveryCheapest = action.deliveryCheapest;
        draft.deliverySoonest = action.deliverySoonest;
        draft.hasAttemped = action.hasAttemped;
        draft.pricingRaw = action.pricingRaw;
    },
    "Affinity/Quantity/Set": (
        draft: Draft<PlaceOrderState>,
        action: { quantity: number },
    ) => {
        draft.quantity = action.quantity;
        draft.currentStep = PlaceOrderStep.SelectAddress;
    },
    "Affinity/Product/Set": (
        draft: Draft<PlaceOrderState>,
        action: { product: AffinityProduct },
    ) => {
        draft.selectedProduct = action.product;
        draft.currentStep = PlaceOrderStep.SelectQuantity;
    },

    // ADDRESS START
    "Affinity/PlaceOrder/SetAddresses": (
        draft: Draft<PlaceOrderState>,
        action: { addressCollection: ShipToCollectionModel },
    ) => {
        draft.currentStep = PlaceOrderStep.SelectAddress; // Maintain second step, select an address to progress
        draft.existingAddresses = action.addressCollection;
    },
    "Affinity/PlaceOrder/SetAddress": (
        draft: Draft<PlaceOrderState>,
        action: { address: ShipToModel },
    ) => {
        draft.currentStep = PlaceOrderStep.SelectPriceAndDeliveryOptions;
        draft.selectedAddress = action.address;
    },
    "Affinity/PlaceOrder/SwitchAddressMode": (
        draft: Draft<PlaceOrderState>,
        action: { value: boolean | undefined }
    ) => {
        if (action.value) {
            draft.isAddingAddress = action.value;
        } else {
            draft.isAddingAddress = !draft.isAddingAddress;
        }
    },

    // ADDRESS END

    "Affinity/PriceAndDeliveryOption/Set": (
        draft: Draft<PlaceOrderState>,
        action: { delivery: AffinityDelivery, phone: string | undefined },
    ) => {
        draft.currentStep = PlaceOrderStep.ReviewOrder;
        draft.priceAndDelivery = action.delivery;
        draft.phone = action.phone;
        const selectedPricingModel = action.delivery === AffinityDelivery.Fastest ? draft.deliverySoonest : draft.deliveryCheapest;
        draft.pricingSelected = draft.pricingRaw?.items?.find(p => `priceID: ${p.harrierSource.priceId.toString()} ,supplierId: ${p.harrierSource.supplierId.toString()}` === selectedPricingModel?.id);
    },
    "Affinity/DeliveryInstructions/Set": (
        draft: Draft<PlaceOrderState>,
        action: { deliveryInstructions: string, phone?: string },
    ) => {
        draft.currentStep = PlaceOrderStep.ConfirmationState;
        draft.deliveryInstructions = `Call ${action.phone || draft.phone} ${action.deliveryInstructions}`;
    },
    "Affinity/PurchaseOrder/Set": (
        draft: Draft<PlaceOrderState>,
        action: { purchaseOrder: string },
    ) => {
        draft.currentStep = PlaceOrderStep.Terminal;
        draft.purchaseOrder = action.purchaseOrder;
    },
    "Affinity/RevertToStep": (
        draft: Draft<PlaceOrderState>,
        action: { step: PlaceOrderStep },
    ) => {
        draft.currentStep = action.step;
        switch (action.step) {
            case PlaceOrderStep.ReviewOrder:
                draft.deliveryInstructions = undefined;
                draft.purchaseOrder = undefined;
                break;
            case PlaceOrderStep.SelectPriceAndDeliveryOptions:
                draft.priceAndDeliveryOptionId = undefined;
                draft.priceAndDeliveryDescription = undefined;
                draft.priceOptionsMapped = undefined;
                draft.deliveryInstructions = undefined;
                draft.purchaseOrder = undefined;
                break;
            case PlaceOrderStep.SelectAddress:
                draft.shipToId = undefined;
                draft.shipToDescription = undefined;
                draft.shipToModel = undefined;
                draft.shipToMapped = undefined;
                draft.priceAndDeliveryOptionId = undefined;
                draft.priceAndDeliveryDescription = undefined;
                draft.priceOptionsMapped = undefined;
                draft.deliveryInstructions = undefined;
                draft.purchaseOrder = undefined;
                draft.pricingRaw = undefined;
                draft.pricingSelected = undefined;
                break;
            case PlaceOrderStep.SelectQuantity:
                draft.quantity = undefined;
                draft.quantityDescription = undefined;
                draft.viewModelQuantity = undefined;
                draft.shipToId = undefined;
                draft.shipToDescription = undefined;
                draft.shipToModel = undefined;
                draft.shipToMapped = undefined;
                draft.priceAndDeliveryOptionId = undefined;
                draft.priceAndDeliveryDescription = undefined;
                draft.priceOptionsMapped = undefined;
                draft.deliveryInstructions = undefined;
                draft.purchaseOrder = undefined;
                draft.pricingRaw = undefined;
                draft.pricingSelected = undefined;
                break;
            case PlaceOrderStep.SelectProduct:
                draft.viewModelQuantity = undefined;
                draft.quantity = undefined;
                draft.quantityDescription = undefined;
                draft.viewModelQuantity = undefined;
                draft.shipToId = undefined;
                draft.shipToDescription = undefined;
                draft.shipToModel = undefined;
                draft.shipToMapped = undefined;
                draft.priceAndDeliveryOptionId = undefined;
                draft.priceAndDeliveryDescription = undefined;
                draft.priceOptionsMapped = undefined;
                draft.deliveryInstructions = undefined;
                draft.purchaseOrder = undefined;
                draft.pricingRaw = undefined;
                draft.pricingSelected = undefined;
                break;
            // *** TODO CLEAR MODELS DOWN ON EACH STEP AS WELL ***
            // PRODUCTS COLLECTION CAN KEEP HOWEVER FOR PERFORMANCE, SAME FOR USERS ORDER HISTORY MAPPINGS
            default:
                break;
        }
    }
}

export default createTypedReducerWithImmer(initialState, reducer);
// export default createTypedReducerWithImmer(mockState, reducer);